import { Component, OnInit, Input, OnChanges, OnDestroy, SimpleChanges, Output, EventEmitter } from '@angular/core';
import { first, forkJoin, of, switchMap, takeUntil } from 'rxjs';
import { Subject } from 'rxjs';
import { CartService } from '../../services/cart/cart.service';
import { GungFlowService } from '../../services/gung-flow/gung-flow.service';
import { GungModalService } from '../../services/gung-modal/gung-modal.service';
import { TranslateService } from '@ngx-translate/core';
import { ProductInputQuantityConfigService } from '../../services/product-input-quantity-config/product-input-quantity-config.service';
import { Product } from '../../models';
import { Availability, SimplifiedAvailability } from '../../models/availability';
import { th } from 'date-fns/locale';
import { CustomerProductPrice } from '../../models/price';
import { AuthService } from '../../services/auth/auth.service';
import { Router } from '@angular/router';
import { GungEventSystemService } from '../../services/gung-event-system/gung-event-system.service';

interface QuantityUpdatedEvent {
  id: string;
  qty: number;
}

@Component({
  selector: 'lib-buy-btn',
  templateUrl: './buy-btn.component.html',
  styleUrls: ['./buy-btn.component.scss'],
  standalone: false
})
export class BuyBtnComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  id: string;

  @Input()
  disabled: boolean;

  @Input()
  disabledAdd: boolean;

  @Input()
  disabledSub: boolean;

  @Input()
  productPartialId?: string;

  @Input()
  product?: Product;

  @Input()
  price?: CustomerProductPrice;

  @Input()
  minimumOrderQuantity: number;

  @Input()
  maximumOrderQuantity: number;

  @Input()
  stepAmount: number;

  @Input()
  targetStockId?: string;

  @Output()
  quantityUpdatedBuyBtn? = new EventEmitter<QuantityUpdatedEvent>();

  currentQty: number;
  previousQty: number;

  allowManualQtyInputOutsideStepSizes = false;

  isOnlyDisplayFlow = false;
  unsubscribe: Subject<void> = new Subject();

  @Input()
  checkoutCart: boolean;

  @Input()
  recomendedInitOrderQuantity?: number;

  @Input()
  availability?: Availability | SimplifiedAvailability;

  @Input()
  tabindex?: number;

  @Input()
  public labelAddToCart = 'ADD_TO_CART'

  isInit = true;

  roles: string[];
  isAnonymous: boolean = false;
  isUser: boolean;
  isSales: boolean;
  isAdmin: boolean;
  isActuator: boolean;
  isSupplier: boolean;
  isBuyer: boolean;

  decimals: number;

  emitQtyChangedEvent: boolean = false;

  constructor(
    protected cartService: CartService,
    protected gungFlowService: GungFlowService,
    protected productInputQuantityConfigService: ProductInputQuantityConfigService,
    protected gungModalService: GungModalService,
    protected translationService: TranslateService,
    protected authService: AuthService,
    protected router: Router,
    protected gungEventSystemService: GungEventSystemService
  ) { }

  ngOnInit() {
    this.authService.getRoles().pipe(
      first(),
      switchMap(roles => forkJoin({
        roles: of(roles),
        currentUser: this.authService.getCurrentUser().pipe(first())
      }))
    ).subscribe(({ roles, currentUser }) => {
      this.roles = roles;
      this.isAnonymous = roles.indexOf('ANONYMOUS') > -1;
      this.isUser = roles.indexOf('USER') > -1 && roles.indexOf('SALES') === -1 && roles.indexOf('ADMIN') === -1 && roles.indexOf('ACTUATOR') === -1;
      this.isSales = roles.indexOf('SALES') > -1;
      this.isAdmin = roles.indexOf('ADMIN') > -1;
      this.isActuator = roles.indexOf('ACTUATOR') > -1;
      this.isSupplier = roles.indexOf('SUPPLIER') > -1;
      this.isBuyer = roles.indexOf('BUYER') > -1;

      if (currentUser?.extra?.blockUserOrdering) {
        this.disabled = true;
      }

      // if flow is only display then do not show the buy-btn, instead the Show product detail
      this.gungFlowService
        .getSelectedFlow()
        .pipe(takeUntil(this.unsubscribe))
        .subscribe(flow => {
          this.isOnlyDisplayFlow = !!flow && !!flow.displayOnly;

          if (
            !!this.availability &&
            this.availability.maxFutureStock === 0 &&
            !this.disabled &&
            flow.disableNonAvailable
          ) {
            this.disabled = true;
          }

          this.setValues();
        });
    })

    this.initQtySubscription();
  }

  initQtySubscription(): void {
    this.cartService
      .getProductQty(this.id, this.productPartialId)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(qty => {
        setTimeout(() => {
          this.emitQtyChangedGungEvent(qty);
          this.currentQty = qty;
          this.previousQty = qty;
          this.quantityUpdatedBuyBtn.emit({ id: this.id, qty: this.currentQty });
        }, 0);
      });
  }

  ngOnChanges() {
    if (this.roles) {
      this.setValues();
    }
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  add() {
    if (!this.currentQty) {
      const toAdd = Math.max(this.minimumOrderQuantity, this.stepAmount, this.recomendedInitOrderQuantity);
      if (!this.productInputQuantityConfigService.getAllowPurchaseOverAvailability(toAdd, this.availability, this.product, this.roles)) {
        if (this.productInputQuantityConfigService.purchaseOverAvailabilitySetMaxQty) {
          const maxValue = this.productInputQuantityConfigService.getMaximumOrderQuantityFromAvailability(this.previousQty, toAdd, this.availability, this.product, this.roles);
          if (maxValue !== this.previousQty) {
            this.cartService.addToCart(this.id, maxValue, this.targetStockId, this.productPartialId, undefined, undefined, undefined, this.product?.name);
            this.emitQtyChangedEvent = true;
          }
        }
        return;
      }
      this.cartService.addToCart(this.id, toAdd, this.targetStockId, this.productPartialId, undefined, undefined, undefined, this.product?.name);
      this.emitQtyChangedEvent = true;
      return;
    }

    const differenceToStep = this.stepAmount - Number(Number((this.currentQty % this.stepAmount)).toFixed(this.decimals));
    if (differenceToStep && differenceToStep !== this.stepAmount) {
      if (!this.productInputQuantityConfigService.getAllowPurchaseOverAvailability(this.currentQty + differenceToStep, this.availability, this.product, this.roles)) {
        if (this.productInputQuantityConfigService.purchaseOverAvailabilitySetMaxQty) {
          const maxValue = this.productInputQuantityConfigService.getMaximumOrderQuantityFromAvailability(this.previousQty, this.currentQty + differenceToStep, this.availability, this.product, this.roles);
          if (maxValue !== this.previousQty) {
            this.cartService.addToCart(this.id, maxValue, this.targetStockId, this.productPartialId, undefined, undefined, undefined, this.product?.name);
            this.emitQtyChangedEvent = true;
          }
        }
        return;
      }
      this.cartService.addToCart(this.id, differenceToStep, this.targetStockId, this.productPartialId, undefined, undefined, undefined, this.product?.name);
      this.emitQtyChangedEvent = true;
      return;
    }

    if (!this.productInputQuantityConfigService.getAllowPurchaseOverAvailability(this.currentQty + this.stepAmount, this.availability, this.product, this.roles)) {
      if (this.productInputQuantityConfigService.purchaseOverAvailabilitySetMaxQty) {
        const maxValue = this.productInputQuantityConfigService.getMaximumOrderQuantityFromAvailability(this.previousQty, this.currentQty + this.stepAmount, this.availability, this.product, this.roles);
        if (maxValue !== this.previousQty) {
          this.cartService.addToCart(this.id, maxValue, this.targetStockId, this.productPartialId, undefined, undefined, undefined, this.product?.name);
          this.emitQtyChangedEvent = true;
        }
      }
      return;
    }
    if (this.productInputQuantityConfigService.getMaxOrderQuantity(this.id, this.product) && this.productInputQuantityConfigService.getMaxOrderQuantity(this.id, this.product) < this.currentQty + this.stepAmount) {
      return;
    }
    this.cartService.addToCart(this.id, this.stepAmount, this.targetStockId, this.productPartialId, undefined, undefined, undefined, this.product?.name);
    this.emitQtyChangedEvent = true;
  }

  sub() {
    if (this.currentQty - this.stepAmount >= this.minimumOrderQuantity) {
      const differenceToStep = this.stepAmount - Number(Number((this.currentQty % this.stepAmount)).toFixed(this.decimals));
      if (differenceToStep && differenceToStep !== this.stepAmount) {
        const quantityToSubtract = this.stepAmount - differenceToStep;
        this.cartService.subtractFromCart(this.id, quantityToSubtract, this.targetStockId, this.productPartialId);
        this.emitQtyChangedEvent = true;
        return;
      }

      this.cartService.subtractFromCart(this.id, this.stepAmount, this.targetStockId, this.productPartialId);
      this.emitQtyChangedEvent = true;
    } else {
      if (this.checkoutCart) {
        this.gungModalService
          .openConfirmYesNoModal(undefined, this.translationService.instant('REMOVE_PRODUCT') + '?', { size: 'sm' })
          .then(result => {
            if (result) {
              this.cartService.subtractFromCart(this.id, this.currentQty, this.targetStockId, this.productPartialId);
              this.emitQtyChangedEvent = true;
            } else {
              this.cartService.setProductQuantity(this.id, this.previousQty, this.targetStockId, this.productPartialId);
            }
          });
      } else {
        const differenceToStep = this.stepAmount - Number(Number((this.currentQty % this.stepAmount)).toFixed(this.decimals));
        if (differenceToStep && differenceToStep !== this.stepAmount) {
          const quantityToSubtract = this.stepAmount - differenceToStep;
          this.cartService.subtractFromCart(this.id, quantityToSubtract, this.targetStockId, this.productPartialId);
          this.emitQtyChangedEvent = true;
          return;
        }

        this.cartService.subtractFromCart(this.id, this.currentQty, this.targetStockId, this.productPartialId);
        this.emitQtyChangedEvent = true;
      }
    }
  }

  set(value: number, ele?: HTMLInputElement) {
    if (this.decimals === 0) {
      value = Math.floor(value);
    } else {
      value = Number(Number(value).toFixed(this.decimals));
    }

    if (!this.productInputQuantityConfigService.getAllowPurchaseOverAvailability(value, this.availability, this.product, this.roles)) {
      const maxValue = this.productInputQuantityConfigService.getMaximumOrderQuantityFromAvailability(this.previousQty, value, this.availability, this.product, this.roles);
      const updateQty = value = maxValue || this.previousQty;
      value = maxValue || this.previousQty;
      if (updateQty) {
        this.cartService.setProductQuantity(this.id, value, this.targetStockId, this.productPartialId);
      }
    } else if (this.maximumOrderQuantity && value > this.maximumOrderQuantity) {
      value = this.maximumOrderQuantity;
      this.cartService.setProductQuantity(this.id, value, this.targetStockId, this.productPartialId);
    } else if (this.productInputQuantityConfigService.getMaxOrderQuantity(this.id, this.product) && value > this.productInputQuantityConfigService.getMaxOrderQuantity(this.id, this.product)) {
      this.cartService.setProductQuantity(this.id, this.productInputQuantityConfigService.getMaxOrderQuantity(this.id, this.product), this.targetStockId, this.productPartialId);
      value = this.productInputQuantityConfigService.getMaxOrderQuantity(this.id, this.product);
    } else if (this.allowManualQtyInputOutsideStepSizes) {
      this.cartService.setProductQuantity(this.id, value, this.targetStockId, this.productPartialId);
    } else if (value >= this.minimumOrderQuantity && Math.abs(value / Number(this.stepAmount) - Math.round(value / Number(this.stepAmount))) < 1e-8) {
      this.cartService.setProductQuantity(this.id, value, this.targetStockId, this.productPartialId);
    } else if (value !== 0 && value <= this.minimumOrderQuantity && this.minimumOrderQuantity > Number(this.stepAmount)) {
      value = this.minimumOrderQuantity;
      this.cartService.setProductQuantity(this.id, value, this.targetStockId, this.productPartialId);
    } else if (value >= 0 && value % this.stepAmount >= Number.EPSILON) {
      value += this.stepAmount - (value % this.stepAmount);
      this.cartService.setProductQuantity(this.id, value, this.targetStockId, this.productPartialId);
      this.emitQtyChangedEvent = true;
    } else {
      if (this.checkoutCart) {
        this.gungModalService
          .openConfirmYesNoModal(undefined, this.translationService.instant('REMOVE_PRODUCT') + '?', { size: 'sm' })
          .then(result => {
            if (result) {
              this.cartService.setProductQuantity(this.id, 0, this.targetStockId, this.productPartialId);
            } else {
              this.cartService.setProductQuantity(this.id, this.previousQty, this.targetStockId, this.productPartialId);
            }
          });
      } else {
        this.cartService.setProductQuantity(this.id, 0, this.targetStockId, this.productPartialId);
      }
    }

    if (ele) {
      ele.value = isNaN(value) ? '' : value.toString();
    }

    this.emitQtyChangedEvent = true;
  }

  formatValue(value: number): string {
    if (value == null) {
      return '';
    }
    return value.toFixed(this.decimals);
  }

  goToDetailUrl(): string {
    if (this.router.url.includes('/articles')) {
      return `${this.id}`;
    }
    return `/product/${this.id}`;
  }

  setValues(): void {
    if (!this.minimumOrderQuantity) {
      this.minimumOrderQuantity = this.productInputQuantityConfigService.getMinimumOrderQuantity(
        this.id,
        this.product,
        this.price,
        this.roles
      );
    }

    this.allowManualQtyInputOutsideStepSizes =
      this.productInputQuantityConfigService.getAllowManualQtyInputOutsideStepSizes(this.id, this.product, this.price, this.isAnonymous, this.isUser, this.isSales, this.isAdmin, this.isActuator);

    if (!this.stepAmount) {
      this.stepAmount = this.productInputQuantityConfigService.getStepAmount(this.id, this.product, this.price, this.roles);
    }

    // this.stepAmount = Math.trunc(this.stepAmount); // REVIEW THIS, THE STEP CAN BE FLOAT !!!
    if (!this.recomendedInitOrderQuantity) {
      this.recomendedInitOrderQuantity = this.productInputQuantityConfigService.getRecomendedInitOrderQuantity(
        this.id,
        this.product,
        this.price
      );
    }

    this.decimals = this.productInputQuantityConfigService.showDecimals(this.id, this.product);

    this.disabled = this.disabled || this.productInputQuantityConfigService.disableBuyBtn(this.product, this.availability);
  }

  edit() {
    this.gungModalService.openEditProductModalComponent(this.id, this.product).then(_ => { }).catch(_ => { });
  }

  emitQtyChangedGungEvent(currentQty: number): void {
    if (this.emitQtyChangedEvent) {
      this.gungEventSystemService.emitGungEvent('cartQuantityChange', { productId: this.id || this.product?.id, currentQty: currentQty, previousQty: this.previousQty });
      this.emitQtyChangedEvent = false;
    }
  }
}
